import { FC, useEffect, useState } from 'react';
import { Switch, Button, Input, Checkbox, Heading, Text, Grid, Box, useClipboard } from "@chakra-ui/react";
import { PackPermission } from 'domains/pack';
import { useToastNotification, usePackShareTokens, usePack } from 'lib/hooks';
import { useActivePackContext } from "lib/context";
import { teamAppUrl } from 'lib/utils';
import { decrypt } from 'lib/e2ee';

type Props = {
  title: string;
  token: any;
  team: any;
  displayToggle?: boolean;
}

const lookup = {
  'read-only': 'view only access now',
  'collaboration': 'permission to collaborate now',
  'admin': 'admin access to this pack now'
}

const adminTokenLookup = {
  'collaboration': 'collaborate on this pack as a guest',
  'admin': 'have guest admin access to this pack'
}

export const Share: FC<Props> = ({ token, title, children, team, displayToggle = true }) => {
  const { activePack } = useActivePackContext();
  const { update } = usePackShareTokens(activePack?.id);
  const { successToast } = useToastNotification();
  const { data: pack } = usePack(activePack?.id);
  const [ key, setKey ] = useState<string>(null);

  useEffect(() => {
    (async () => {
      if (!token.key || !pack?.key) return;

      setKey(await decrypt(token.key, pack.key));
    })();
  }, [token.key, pack, setKey]);

  const hasPwd = token.permission !== PackPermission.READ_ONLY && token?.anon_enabled;

  const shareLink = `${teamAppUrl(team?.identifier)}/packs/${token?.id}${hasPwd ? `?pwd=${token.anon_pwd}` : ''}${key ? `#${key}` : ''}`;
  const { hasCopied, onCopy } = useClipboard(shareLink);

  const handleEnable = async (enabled: boolean): Promise<void> => {
    await update({ ...token, enabled });
  }

  const handleTeamShare = async (team_share: boolean): Promise<void> => {
    await update({ ...token, team_share });
  }

  const handleAnonEnabled = async (isEnabled: boolean): Promise<void> => {
    await update({ ...token, anon_enabled: isEnabled });
  }

  const handleIndexEnabled = async (isEnabled: boolean): Promise<void> => {
    await update({ ...token, index_enabled: isEnabled });
  }

  const isReadOnlyToken = token.permission === PackPermission.READ_ONLY;

  useEffect(() => {
    if (!hasCopied) return;

    successToast({
      title: "Success",
      description: "Link copied to clipboard"
    });
  }, [successToast, hasCopied]);

  return (
    <>
      <Grid width="100%" gap="20px" justifyContent="space-between" alignItems={"center"} gridTemplateColumns="auto auto">
        <Box w="auto" flex="1">
          <Heading size="sm" color="brand.500" mb={2}>{ title }</Heading>
          { children }
        </Box>
        <Box>
          {displayToggle && <Switch id="email-alerts" size="lg" isChecked={token.enabled} onChange={(event) => handleEnable(event.target.checked)} />}
        </Box>
      </Grid>
      {token.enabled && (
        <>
          <Grid gap="20px" justifyContent="space-between" gridTemplateColumns="1fr 70px" mt="20px">
            <Box w="auto" flex="1">
              <Input size="sm" isDisabled={true} value={shareLink} />
            </Box>
            <Box>
              <Button h="100%" w="100%" variant="primary" size="sm" onClick={onCopy}>
                { hasCopied ? 'Copied' : 'Copy' }
              </Button>
            </Box>
          </Grid>
          <Box pt="20px">
            <Checkbox isChecked={token.team_share} onChange={(event) => handleTeamShare(event.target.checked)}>
              <Text size="sm">Give your entire {team?.name} team {lookup[token.permission]}</Text>
            </Checkbox>
            {!isReadOnlyToken && (
              <Checkbox isChecked={token.anon_enabled} onChange={(event) => handleAnonEnabled(event.target.checked)}>
                <Text size="sm">Let anyone with this link {adminTokenLookup[token.permission]}</Text>
              </Checkbox>
            )}
            {isReadOnlyToken && (
              <Checkbox isChecked={token.index_enabled} onChange={(event) => handleIndexEnabled(event.target.checked)}>
                <Text size="sm">Make this pack discoverable on search engines (Google, Bing, Yahoo!)</Text>
              </Checkbox>
            )}
          </Box>
        </>
      )}
    </>
  )
}
